import React, { useState, useEffect, useContext } from 'react';
import { css } from '@emotion/react';
import {
  Alert,
  Box,
  Button,
  Flex,
  Panel,
  Spinner,
  theme,
  Text,
} from '@xometry/xometry_loft';
import DisplayShippingAddresses from './DisplayShippingAddress';
import EditShippingAddressForm from './EditShippingAddressForm';
import usersApi from '../../services/users';
import { useGetTargetUserData } from '../../contexts/User/UserContext';
import UserShippingAccounts from '../ShippingAccounts';
import {
  AddShippingContainer,
  addShippingImage,
  shippingAddress,
  ShippingAddressPanel,
} from './ShippingAddress.styles';
import {
  CreateOrganizationShippingAddressMutationResult,
  CreateOrganizationShippingAddressMutationVariables,
  CreateOrganizationShippingAddressQuery,
  DeleteOrganizationShippingAddressByIdMutationResult,
  DeleteOrganizationShippingAddressByIdMutationVariables,
  DeleteOrganizationShippingAddressByIdQuery,
  GetOrganizationShippingAddressesByOrganizationIdQuery,
  GetOrganizationShippingAddressesByOrganizationIdQueryResult,
  OrganizationShippingAddress,
  UpdateOrganizationShippingAddressByIdMutationResult,
  UpdateOrganizationShippingAddressByIdQuery,
  GetOrganizationShippingAccountsByOrganizationIdQuery,
  GetOrganizationShippingAccountsByOrganizationIdQueryResult,
  UpdateOrganizationShippingAddressByIdMutationVariables,
  GetOrganizationUsersQueryResult,
  GetOrganizationUsersQuery,
} from '../../schema/generated/schema';
import { useMutation, useQuery } from '@apollo/client';
import { ShippingAccountsTableProps } from '../ShippingAccounts/ShippingAccountTable';
import AddressCorrectionModal from './AddressCorrectionModal/AddressCorrectionModal';
import { enteredAddressIsValid, mapSmartyAddress } from './utilities';
import {
  MappedShippingAddress,
  UserShippingAddressResponse,
  UserShippingAddressPayload,
  userRoleEnum,
  entityTypeEnum,
} from './types';
import useAddressAutoComplete from '../../hooks/useAddressAutocomplete';
import AuthContext from '@xometry/auth-context';

interface Props {
  entityID: string; // this can be a user/team ID
  entityType: entityTypeEnum;
  displayNotification: (type: string, message: string) => void;
  onShippingAddressUpdate: () => void;
  onOnboardingActionCompleted?: () => void;
}

const ShippingAddressBlock: React.FC<Props> = ({
  entityID,
  entityType,
  displayNotification,
  onShippingAddressUpdate,
  onOnboardingActionCompleted = () => undefined,
}: Props) => {
  const { user } = useContext(AuthContext);
  const [showForm, setShowForm] = useState(false);
  const [editShippingAddressIndex, setEditShippingAddressIndex] = useState<
    number | null
  >(null);
  const [shippingAddresses, setShippingAddresses] = useState<
    MappedShippingAddress[]
  >([]);
  const [requestFailed, setRequestFailed] = useState(false);
  const [teamShippingAccounts, setTeamShippingAccounts] = useState<
    ShippingAccountsTableProps[]
  >([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [noSmartyAddressFound, setNoSmartyAddressFound] = useState(false);
  const [addressToModify, setAddressToModify] =
    useState<MappedShippingAddress | null>(null);
  const [userRole, setUserRole] = useState<userRoleEnum | null>(null);

  // We want to map all user and team shipping addresses to same object. We want camel case.
  const mapShippingAddresses = (
    shippingAddress: OrganizationShippingAddress | UserShippingAddressResponse,
    index = -1
  ): MappedShippingAddress => {
    const mappedShippingAddress: MappedShippingAddress = {
      name: '',
      street1: '',
      country: '',
      countryCode: '',
      city: '',
      zipcode: '',
      phoneNumber: '',
      emailAddress: '',
      validated: false,
      validationExempt: false,
      defaultIndicator: false,
    };

    mappedShippingAddress['id'] =
      'id' in shippingAddress ? shippingAddress?.id : '';
    mappedShippingAddress['organizationId'] =
      'organizationId' in shippingAddress
        ? shippingAddress?.organizationId
        : '';
    mappedShippingAddress['name'] = shippingAddress?.name;
    mappedShippingAddress['street1'] = shippingAddress?.street1;
    mappedShippingAddress['street2'] = shippingAddress?.street2 || '';
    mappedShippingAddress['street3'] = shippingAddress?.street3 || '';
    mappedShippingAddress['country'] =
      'country' in shippingAddress ? shippingAddress?.country : '';
    mappedShippingAddress['countryCode'] =
      entityType === entityTypeEnum.user
        ? 'country_code' in shippingAddress
          ? shippingAddress?.country_code
          : ''
        : 'countryCode' in shippingAddress
        ? shippingAddress?.countryCode
        : '';
    mappedShippingAddress['city'] = shippingAddress?.city;
    mappedShippingAddress['state'] = shippingAddress?.state;
    mappedShippingAddress['province'] =
      'province' in shippingAddress ? shippingAddress?.province : '';
    mappedShippingAddress['zipcode'] = shippingAddress?.zipcode;
    mappedShippingAddress['phoneNumber'] =
      entityType === entityTypeEnum.user
        ? 'phone_number' in shippingAddress
          ? shippingAddress?.phone_number
          : ''
        : 'phoneNumber' in shippingAddress
        ? shippingAddress?.phoneNumber
        : '';
    mappedShippingAddress['phoneExt'] = shippingAddress?.phone_ext || '';
    mappedShippingAddress['emailAddress'] =
      entityType === entityTypeEnum.user
        ? 'email_address' in shippingAddress
          ? shippingAddress?.email_address
          : ''
        : 'emailAddress' in shippingAddress
        ? shippingAddress?.emailAddress
        : '';
    mappedShippingAddress['internationalVerification'] = false;
    mappedShippingAddress['defaultIndicator'] =
      entityType === entityTypeEnum.user
        ? index === 0
        : 'isTeamDefault' in shippingAddress
        ? shippingAddress?.isTeamDefault
        : false;
    mappedShippingAddress['validated'] =
      entityType === entityTypeEnum.user
        ? false
        : 'validated' in shippingAddress
        ? !!shippingAddress?.validated
        : false;
    mappedShippingAddress['validationExempt'] =
      entityType === entityTypeEnum.user
        ? false
        : 'validationExempt' in shippingAddress
        ? !!shippingAddress?.validationExempt
        : false;

    return mappedShippingAddress;
  };

  const {
    userData,
    isLoading: isUserLoading,
    error: userError,
  } = entityType === entityTypeEnum.user
    ? useGetTargetUserData()
    : { userData: null, isLoading: false, error: null };

  // query to get team shipping addresses by organizationId
  const {
    loading: teamShippingAddressesLoading,
    data: teamShippingAddressesData,
  } = useQuery<GetOrganizationShippingAddressesByOrganizationIdQueryResult>(
    GetOrganizationShippingAddressesByOrganizationIdQuery,
    {
      skip: entityType !== entityTypeEnum.team,
      variables: {
        organizationId: entityID,
      },
    }
  );

  // query to get team shipping accounts by organizationId
  const {
    loading: teamShippingAccountsLoading,
    data: teamShippingAccountsData,
    refetch: refetchTeamShippingAccountsData,
  } = useQuery<GetOrganizationShippingAccountsByOrganizationIdQueryResult>(
    GetOrganizationShippingAccountsByOrganizationIdQuery,
    {
      skip: entityType !== entityTypeEnum.team,
      variables: {
        organizationId: entityID,
      },
    }
  );

  // query to get team users
  const { data: teamUsersData, loading: teamUsersLoading } =
    useQuery<GetOrganizationUsersQueryResult>(GetOrganizationUsersQuery, {
      skip: entityType !== entityTypeEnum.team,
      variables: {
        orgId: entityID,
      },
    });

  // mutation to add a new team shipping address
  const [createOrganizationShippingAddress] = useMutation<
    CreateOrganizationShippingAddressMutationResult,
    CreateOrganizationShippingAddressMutationVariables
  >(CreateOrganizationShippingAddressQuery, {
    refetchQueries: [
      GetOrganizationShippingAddressesByOrganizationIdQuery,
      'GetOrganizationShippingAddressesByOrganizationId',
    ],
  });

  // mutation to update a team shipping address
  const [updateOrganizationShippingAddressById] = useMutation<
    UpdateOrganizationShippingAddressByIdMutationResult,
    UpdateOrganizationShippingAddressByIdMutationVariables
  >(UpdateOrganizationShippingAddressByIdQuery, {
    refetchQueries: [
      GetOrganizationShippingAddressesByOrganizationIdQuery,
      'GetOrganizationShippingAddressesByOrganizationId',
    ],
  });

  // mutation to delete a team shipping address
  const [deleteOrganizationShippingAddressById] = useMutation<
    DeleteOrganizationShippingAddressByIdMutationResult,
    DeleteOrganizationShippingAddressByIdMutationVariables
  >(DeleteOrganizationShippingAddressByIdQuery, {
    refetchQueries: [
      GetOrganizationShippingAddressesByOrganizationIdQuery,
      'GetOrganizationShippingAddressesByOrganizationId',
    ],
  });

  // get user addresses only when entityType is "user"
  useEffect(() => {
    if (entityType === entityTypeEnum.user && !isUserLoading && !userError) {
      const shippingAddresses: UserShippingAddressResponse[] =
        (userData?.shipping_addresses as UserShippingAddressResponse[]) || [];
      const mappedShippingAddresses = shippingAddresses.map(
        (shippingAddress, i) => mapShippingAddresses(shippingAddress, i)
      );
      setShippingAddresses(mappedShippingAddresses);
    }
  }, [isUserLoading, userError, userData, entityType]);

  const { queryStreetValidation, suggestedAddress, clearSuggestedAddress } =
    useAddressAutoComplete();

  // get team shipping accounts
  useEffect(() => {
    if (entityType === entityTypeEnum.team && teamShippingAccountsData) {
      const accounts =
        teamShippingAccountsData.getOrganizationShippingAccountsByOrganizationId
          .filter((account) => !account?.isDeleted)
          .map((account) => ({
            id: account?.id || '',
            account_number: account?.accountNumber || '',
            provider: account?.provider || '',
            isDefault: account?.isTeamDefault || false,
          }));
      setTeamShippingAccounts(accounts);
    }
  }, [teamShippingAccountsData, entityType]);

  // get team shipping addresses
  useEffect(() => {
    if (entityType === entityTypeEnum.team && teamShippingAddressesData) {
      const addresses: OrganizationShippingAddress[] = (
        teamShippingAddressesData.getOrganizationShippingAddressesByOrganizationId as OrganizationShippingAddress[]
      ).filter((address) => !address?.isDeleted);
      const mappedShippingAddresses = addresses.map((shippingAddress) =>
        mapShippingAddresses(shippingAddress)
      );

      // Sorting the ones with defaultIndicator first, so they match the user shipping addresses (they come ordered by default from the backend)
      const sortedShippingAddresses = mappedShippingAddresses.sort((a, b) => {
        return a.defaultIndicator ? -1 : b.defaultIndicator ? 1 : 0;
      });

      setShippingAddresses(sortedShippingAddresses);
    }
  }, [teamShippingAddressesData, entityType]);

  // get user role
  useEffect(() => {
    if (entityType === entityTypeEnum.team && teamUsersData) {
      const currentTeamUser = teamUsersData.getOrganizationUsers.find(
        (teamUser) => {
          return teamUser?.userId === user?.id;
        }
      );
      const role = currentTeamUser?.role.name.toLowerCase();
      setUserRole(
        role === userRoleEnum.admin || role === userRoleEnum.contributor
          ? role
          : null
      );
    }
  }, [teamUsersData, entityType, user]);

  const handleAddressValidation = async (
    enteredAddress: MappedShippingAddress
  ) => {
    if (!enteredAddress) {
      displayNotification('error', 'Address is missing.');
      return false;
    }

    if (
      enteredAddress.countryCode !== 'US' ||
      Boolean(enteredAddress.validated) === true ||
      Boolean(enteredAddress.validationExempt) === true
    ) {
      return true; // Assume validation passed
    }
    setAddressToModify(enteredAddress);
    return queryStreetValidation(
      `${enteredAddress.street1}, ${enteredAddress.street2 ?? ''}, ${
        enteredAddress.city
      }, ${enteredAddress.state || enteredAddress.province}, ${
        enteredAddress.zipcode
      }`
    ).then(async (validatedAddress) => {
      if (!validatedAddress?.components) {
        // Smarty returned no candidates - save the address as validation exempt
        await handleFinish({
          ...enteredAddress,
          validated: false,
          validationExempt: true,
        });
        return;
      }
      const candidate: MappedShippingAddress = mapSmartyAddress(
        validatedAddress.components,
        enteredAddress
      );
      if (enteredAddressIsValid(candidate, enteredAddress)) {
        // Mark as validated to prevent infinite loop when handleFinish calls postUpdateShipping/postAddShipping
        await handleFinish({ ...enteredAddress, validated: true });
        return;
      }
      // Smarty has a different suggestion - show modal for user to choose
      setAddressToModify(enteredAddress);
      setModalOpen(true);
      return false;
    });
  };

  const handleAddressCorrectionModalClose = () => {
    setModalOpen(false);
    (
      document.getElementById('loft-dialog-modal') as HTMLDialogElement
    )?.close();
    setNoSmartyAddressFound(false);
    clearSuggestedAddress();
  };

  const activateEditShipping = (shippingIndex: number) => {
    setEditShippingAddressIndex(shippingIndex);
    setShowForm(false);
    setRequestFailed(false);
  };

  const deactivateEditShipping = () => {
    setEditShippingAddressIndex(null);
    setRequestFailed(false);
  };

  const activateAddShipping = () => {
    setEditShippingAddressIndex(null);
    setShowForm(true);
  };

  const handleApiError = () => {
    setRequestFailed(true);
    displayNotification('error', 'An error occurred, please try again.');
  };

  const setStateValueForTeamPayload = (address: MappedShippingAddress) => {
    // APP-57638: we're using State to store Province for teams
    let state: string;
    if (address.countryCode === 'US') {
      state = address.state || '';
    } else if (address.countryCode === 'CA') {
      state = address.province || '';
    } else {
      state = '';
    }
    return state;
  };

  const postUpdateShipping = async (address: MappedShippingAddress) => {
    const isValid = await handleAddressValidation(address);
    if (!isValid) {
      return;
    }

    if (entityType === entityTypeEnum.user) {
      const payload: UserShippingAddressPayload = {
        address: {
          street1: address.street1,
          street2: address.street2,
          street3: address.street3,
          name: address.name,
          zipcode: address.zipcode,
          province: address.province,
          city: address.city,
          country: address.country,
          state: address.state,
          country_code: address.countryCode,
          phone_number: address.phoneNumber,
          phone_ext: address.phoneExt,
          email_address: address.emailAddress,
        },
        index: editShippingAddressIndex,
      };
      address.countryCode !== 'CA' && delete payload.address.province;
      address.countryCode !== 'US' && delete payload.address.state;
      payload.set_default_address = address.defaultIndicator;

      return usersApi
        .postUpdateShipping(entityID, payload)
        .then(() => {
          displayNotification('success', 'Shipping Information Updated');
          onShippingAddressUpdate();
        })
        .catch(handleApiError);
    } else if (entityType === entityTypeEnum.team) {
      const stateValue = setStateValueForTeamPayload(address);
      updateOrganizationShippingAddressById({
        variables: {
          id: address.id || '',
          organizationId: entityID,
          shippingAddress: {
            name: address.name,
            street1: address.street1,
            street2: address.street2 || '',
            city: address.city,
            state: stateValue, // APP-57638: we're using State to store Province for teams
            countryCode: address.countryCode,
            zipcode: address.zipcode,
            organizationId: entityID,
            emailAddress: address.emailAddress,
            phoneNumber: address.phoneNumber,
            isTeamDefault: address.defaultIndicator,
            validated: address.validated,
            validationExempt: address.validationExempt,
          },
        },
      })
        .then(() => {
          displayNotification('success', 'Team Shipping Information Updated');
          setShowForm(false);
          setEditShippingAddressIndex(null);
        })
        .catch(handleApiError);
    }
  };

  const postAddShipping = async (address: MappedShippingAddress) => {
    address.countryCode !== 'CA' && delete address.province;
    address.countryCode !== 'US' && delete address.state;
    const isValid = await handleAddressValidation(address);
    if (!isValid) {
      return;
    }

    if (entityType === entityTypeEnum.user) {
      const payload: UserShippingAddressPayload = {
        address: {
          street1: address.street1,
          street2: address.street2,
          street3: address.street3,
          name: address.name,
          zipcode: address.zipcode,
          province: address.province,
          city: address.city,
          state: address.state,
          country: address.country,
          country_code: address.countryCode,
          phone_number: address.phoneNumber,
          phone_ext: address.phoneExt,
          email_address: address.emailAddress,
        },
      };
      payload.set_default_address = address.defaultIndicator;

      return usersApi
        .postAddShipping(entityID, payload)
        .then(() => {
          displayNotification('success', 'Shipping Information Added');
          onShippingAddressUpdate();
          onOnboardingActionCompleted();
        })
        .catch(handleApiError);
    } else if (entityType === entityTypeEnum.team) {
      const stateValue = setStateValueForTeamPayload(address);
      // No street3 and phone_ext for org address
      createOrganizationShippingAddress({
        variables: {
          shippingAddress: {
            name: address.name,
            street1: address.street1,
            street2: address.street2 || '',
            city: address.city,
            state: stateValue, // APP-57638: we're using State to store Province for teams
            zipcode: address.zipcode,
            countryCode: address.countryCode,
            organizationId: entityID,
            emailAddress: address.emailAddress,
            phoneNumber: address.phoneNumber,
            isTeamDefault: address.defaultIndicator,
            validated: false,
            validationExempt: false,
          },
        },
      })
        .then(() => {
          displayNotification('success', 'Team Shipping Information Added');
          setShowForm(false);
          onOnboardingActionCompleted();
        })
        .catch(handleApiError);
    }
  };

  const postRemoveShipping = (index: number, id?: string) => {
    if (entityType === entityTypeEnum.user) {
      return usersApi
        .postRemoveShipping(entityID, { index })
        .then(() => {
          displayNotification('success', 'Shipping Information Removed');
          onShippingAddressUpdate();
        })
        .catch(handleApiError);
    } else if (entityType === entityTypeEnum.team) {
      deleteOrganizationShippingAddressById({
        variables: {
          id: id || '',
          organizationId: entityID,
        },
      })
        .then(() => {
          displayNotification('success', 'Team Shipping Address Removed');
          setShowForm(false);
          setEditShippingAddressIndex(null);
        })
        .catch(handleApiError);
    }
  };

  const handleFinish = async (enteredAddress: MappedShippingAddress) => {
    const existingAddress =
      entityType === entityTypeEnum.user
        ? shippingAddresses.find(
            (_address, index) => index === editShippingAddressIndex
          )
        : !!enteredAddress.id;

    if (existingAddress) {
      await postUpdateShipping(enteredAddress);
      handleAddressCorrectionModalClose();
    } else {
      await postAddShipping(enteredAddress);
      handleAddressCorrectionModalClose();
    }
  };

  if (
    isUserLoading ||
    userError ||
    teamShippingAccountsLoading ||
    teamShippingAddressesLoading ||
    teamUsersLoading
  )
    return <Spinner />;

  return (
    <>
      <Panel kind="primary" css={shippingAddress}>
        {userRole && userRole == userRoleEnum.contributor && (
          <Flex>
            <Alert kind={theme.kind.primary}>
              <i
                className="far fa-info-circle"
                css={css`
                  color: ${theme.colors.blues[100]};
                  margin-left: 4px;
                  margin-right: ${theme.space[1]};
                  font-size: 13px;
                `}
              />
              Contact a Team Admin to update team shipping address.
            </Alert>
          </Flex>
        )}
        {shippingAddresses.map((address: MappedShippingAddress, index) =>
          index === editShippingAddressIndex ? (
            <EditShippingAddressForm
              showMode="Edit"
              key={`sa-${index}`}
              onSave={postUpdateShipping}
              shippingAddress={address}
              onCancelClick={deactivateEditShipping}
              onRemove={() => {
                if ('id' in address) {
                  postRemoveShipping(index, address.id);
                } else {
                  postRemoveShipping(index);
                }
              }}
              requestFailed={requestFailed}
              entityType={entityType}
              index={index}
            />
          ) : (
            <DisplayShippingAddresses
              key={`sa-${index}`}
              onEditClick={activateEditShipping}
              address={address}
              index={index}
              entityType={entityType}
              userRole={userRole}
            />
          )
        )}
        {showForm ? (
          <EditShippingAddressForm
            showMode="New"
            onSave={postAddShipping}
            onCancelClick={() => setShowForm(false)}
            requestFailed={requestFailed}
            entityType={entityType}
            index={shippingAddresses.length === 0 ? 0 : 1}
          />
        ) : (
          (userRole !== userRoleEnum.contributor ||
            entityType === entityTypeEnum.user) && (
            <ShippingAddressPanel
              kind="primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <AddShippingContainer onClick={activateAddShipping}>
                <Box marginBottom={theme.space[2]}>
                  <img
                    src="https://s3.amazonaws.com/assets.xometry.com/images/Spot-Hand+Truck.svg"
                    css={addShippingImage}
                  />
                </Box>
                <Button kind={theme.kind.primaryOutline}>
                  <Flex alignItems="center">
                    <Box fontSize={theme.space[4]}>
                      <i className="far fa-plus-circle" />
                    </Box>
                    <Box marginLeft={theme.space[1]}>Add Shipping Address</Box>
                  </Flex>
                </Button>
              </AddShippingContainer>
            </ShippingAddressPanel>
          )
        )}
      </Panel>
      <Box width="100%">
        <Text kind="subpage-title">Shipping Accounts</Text>
        <Panel kind="primary" marginTop={theme.space[4]}>
          {entityType === 'user' ? (
            <UserShippingAccounts
              entityID={entityID}
              entityType={entityType}
              shippingAccounts={userData?.shipping_accounts || []}
              approved3rdPartyShipping={
                userData?.approved_3rd_party_shipping || false
              }
              refreshAuth={onShippingAddressUpdate}
              userRole={userRole}
              onOnboardingActionCompleted={onOnboardingActionCompleted}
            />
          ) : (
            <UserShippingAccounts
              entityID={entityID}
              entityType={entityType}
              shippingAccounts={teamShippingAccounts}
              approved3rdPartyShipping={true}
              refreshAuth={refetchTeamShippingAccountsData}
              userRole={userRole}
              onOnboardingActionCompleted={onOnboardingActionCompleted}
            />
          )}
        </Panel>
      </Box>
      {addressToModify && suggestedAddress && (
        <AddressCorrectionModal
          modalOpen={modalOpen}
          handleClose={handleAddressCorrectionModalClose}
          enteredAddress={addressToModify}
          smartySuggestedAddress={suggestedAddress}
          noSmartyAddressFound={noSmartyAddressFound}
          saveAddress={handleFinish}
        />
      )}
    </>
  );
};

export default ShippingAddressBlock;
